// Lightweight Add-to-Home-Screen prompt — appears only after onboarding is
// complete, only once per dismissal, and only when the browser actually
// fires `beforeinstallprompt` (Chrome/Edge/Android). Safari users get a
// soft hint via a different code path (see iOS detection below).
import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import storage from "../lib/storage";

const DISMISS_KEY = "sm_a2hs_dismissed_at";

function isIOSSafari() {
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && isSafari;
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (!storage.isOnboardingComplete() || isStandalone()) return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - dismissed < 1000 * 60 * 60 * 24 * 14) return;   // 2-week cooldown

    function onBefore(e) {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    }
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari never fires beforeinstallprompt — show a hint instead.
    if (isIOSSafari()) setIosHint(true);

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setIosHint(false);
  }

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) { /* */ }
    setShow(false);
    setDeferred(null);
  }

  if (!show && !iosHint) return null;

  return (
    <div
      data-testid="install-prompt"
      style={{
        position: "fixed", left: 12, right: 12, bottom: 84,
        background: "#fff", border: "1px solid var(--sm-line)",
        borderRadius: 18, padding: "12px 14px",
        boxShadow: "0 18px 36px -14px rgba(107,45,94,0.35)",
        zIndex: 50, display: "flex", alignItems: "center", gap: 12,
      }}
      role="region" aria-label="Install SkinMemory"
    >
      <span
        className="inline-flex items-center justify-center"
        style={{ width: 36, height: 36, borderRadius: 10, background: "#FBEAF0" }}
        aria-hidden
      >
        <Download size={18} color="var(--sm-plum)" />
      </span>
      <div className="flex-1">
        <p style={{ fontSize: 13, fontWeight: 700, color: "var(--sm-ink)" }}>
          Install SkinMemory
        </p>
        <p style={{ fontSize: 11, color: "var(--sm-ink-soft)", marginTop: 1 }}>
          {iosHint
            ? "Tap the Share icon → Add to Home Screen"
            : "One-tap launch, works offline."}
        </p>
      </div>
      {!iosHint && (
        <button
          onClick={install}
          data-testid="install-prompt-cta"
          aria-label="Install SkinMemory"
          style={{
            background: "var(--sm-plum)", color: "#fff",
            height: 36, padding: "0 14px", borderRadius: 999,
            fontSize: 12, fontWeight: 700, minHeight: 48, minWidth: 48,
          }}
        >
          Install
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="install-prompt-dismiss"
        aria-label="Dismiss install prompt"
        style={{
          width: 48, height: 48,
          background: "transparent", color: "var(--sm-mute)",
          borderRadius: 999,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
